// allowlist of money-bearing prop names rendered by @engyalo/delivery-ui-components
const PRICE_KEYS = new Set<string>([
  'price',
  'priceTotal',
  'unitPrice',
  'packagePrice',
  'basePrice',
  'subtotal',
  'total',
  'totalFee',
  'fee',
  'discount',
  'discounts',
])

// keys whose object value is a Price config ({ value, ... }); recursing into
// them masks the inner `value` + forces hideOnZero. A bare numeric under these
// keys is NOT zeroed (e.g. a promotion's `amount` is behavior data, not a price)
const PRICE_CONFIG_KEYS = new Set<string>(['price', 'priceTotal', 'amount'])

const MONETARY_NAMES = new Set<string>([
  'subtotal',
  'discounts',
  'taxes',
  'perceptions',
  'administrativeServices',
  'total',
  'fee',
])

const isPlainRecord = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

const maskNode = (
  node: unknown,
  inPriceConfig: boolean,
  preserveTotal: boolean,
): unknown => {
  if (Array.isArray(node)) {
    return node.map((item) => maskNode(item, inPriceConfig, preserveTotal))
  }

  if (isPlainRecord(node)) {
    // action args feed action handlers at runtime, not displayed prices
    if (node.cinnamonActions) {
      return node
    }

    const isMonetaryNamedItem =
      typeof node.name === 'string' && MONETARY_NAMES.has(node.name)

    const result: Record<string, unknown> = {}

    Object.keys(node).forEach((key) => {
      const value = node[key]
      const isPriceConfigKey = PRICE_CONFIG_KEYS.has(key)

      if (typeof value === 'number' && PRICE_KEYS.has(key)) {
        // caller preserves the MinAmount bar's `total` (drives the progress
        // fill, not a displayed price) — only the node holding `minAmount`
        result[key] =
          preserveTotal && key === 'total' && 'minAmount' in node ? value : 0
      } else if (
        key === 'value' &&
        typeof value === 'number' &&
        (inPriceConfig || isMonetaryNamedItem)
      ) {
        result[key] = 0
      } else if (value && typeof value === 'object') {
        result[key] = maskNode(value, isPriceConfigKey, preserveTotal)
      } else {
        result[key] = value
      }
    })

    if (inPriceConfig) {
      result.hideOnZero = true
    }

    return result
  }

  return node
}

const maskPricesForDisplay = <T extends Record<string, unknown>>(
  props: T,
  preserveTotal = false,
): T => maskNode(props, false, preserveTotal) as T

export default maskPricesForDisplay
